import React, { useState, useMemo } from 'react';

export type API = {
	currentStep: number;
	totalSteps: number;
	next: () => void;
	back: () => void;
	exit: () => void;
	complete: () => void;
};

export type SpotlightProps = {
	api: API;
};

export type SpotlightType = Flow.AbstractComponent<SpotlightProps>;

export type PropsType = {
	spotlights: SpotlightType[];
	onStepChange?: (stepIndex: number) => void;
	onExit: () => void;
	onComplete: () => void;
};

const createCachedSpotlightGetter = (spotlights: SpotlightType[]) => {
	let lastIndex = 0;
	let spotlight = spotlights[lastIndex];
	return (index: number) => {
		if (index !== lastIndex) {
			spotlight = spotlights[index];
			lastIndex = index;
		}
		return spotlight;
	};
};

export const SpotlightListStepper = ({
	spotlights,
	onStepChange,
	onComplete,
	onExit,
}: PropsType) => {
	const [stepIndex, _setStepIndex] = useState(0);

	// recreate the cached spotlight getter when the list of spotlights changes
	const getSpotlightAtIndex = useMemo(() => createCachedSpotlightGetter(spotlights), [spotlights]);

	const setStepIndex = (newValue: number) => {
		if (newValue !== stepIndex) {
			if (onStepChange) {
				onStepChange(newValue);
			}
			_setStepIndex(newValue);
		}
	};

	const api: API = {
		currentStep: stepIndex + 1,
		totalSteps: spotlights.length,
		next: () => {
			setStepIndex(Math.min(stepIndex + 1, spotlights.length - 1));
		},
		back: () => {
			setStepIndex(Math.max(stepIndex - 1, 0));
		},
		exit: onExit,
		complete: onComplete,
	};

	const Spotlight = getSpotlightAtIndex(stepIndex);
	return <Spotlight api={api} />;
};
